import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Observable, Subject, of, shareReplay, tap } from 'rxjs';
import { ConsentResponse, CreateConsentRequest, UpdateConsentRequest, ConsentType } from '../models/consent.model';
import { SharedApiService, ApiType } from './api.service';
import { isPlatformBrowser } from '@angular/common';

interface UserConsent {
  consentType: string;
  grantedAt?: string;
  CreatedAt?: string;
}

interface StoredUser {
  consents?: UserConsent[];
  aiMatching?: boolean;
  aiParsing?: boolean;
  aiParsingConsent?: boolean;
  marketing?: boolean;
  privacyPolicy?: boolean;
}

interface SetConsentOptions {
  jobsRequest?: boolean;
  language?: string;
  version?: string;
  purposeDescription?: string;
  withdrawalReason?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConsentService {
  private consentsCache$: Observable<ConsentResponse[]> | null = null;
  private readonly platformId = inject(PLATFORM_ID);

  // Observable for consent changes (emit when consents are updated)
  private consentChangedSubject = new Subject<ConsentType>();
  public consentChanged$ = this.consentChangedSubject.asObservable();

  constructor(private readonly sharedApiService: SharedApiService) {}

  /**
   * Get all user consents (includes missing consents with null IDs)
   * Uses caching to prevent multiple simultaneous API calls
   */
  getUserConsents(jobsRequest = false): Observable<ConsentResponse[]> {
    if (!this.consentsCache$) {
      this.consentsCache$ = this.sharedApiService.get<ConsentResponse[]>('api/consents', undefined, { apiType: ApiType.OPEN, jobsRequest: jobsRequest })
        .pipe(
          shareReplay({ bufferSize: 1, refCount: true })
        );
    }
    return this.consentsCache$;
  }

  /**
   * Clear the cache (call this when consents are updated)
   */
  private clearCache(): void {
    this.consentsCache$ = null;
  }

  /**
   * Create a new consent (only if granted=true)
   */
  createConsent(request: CreateConsentRequest, jobsRequest = false): Observable<ConsentResponse> {
    return this.sharedApiService.post<ConsentResponse>('api/consents', request, undefined, { apiType: ApiType.OPEN, jobsRequest: jobsRequest })
      .pipe(
        tap(() => this.clearCache())
      );
  }

  /**
   * Update an existing consent (grant/withdraw)
   */
  updateConsent(id: number, request: UpdateConsentRequest, jobsRequest = false): Observable<ConsentResponse> {
    return this.sharedApiService.put<ConsentResponse>(`api/consents/${id}`, request, undefined, { apiType: ApiType.OPEN, jobsRequest: jobsRequest })
      .pipe(
        tap(() => this.clearCache())
      );
  }

  /**
   * Create or update a consent from a list returned by getUserConsents.
   * Missing consents can only be created as granted=true; keeping a missing
   * consent off is a no-op because the backend intentionally stores no row.
   */
  setUserConsent(
    consents: ConsentResponse[],
    consentType: ConsentType,
    granted: boolean,
    options: SetConsentOptions = {}
  ): Observable<ConsentResponse> {
    const existingConsent = consents.find((consent) => consent.consentType === consentType);
    const jobsRequest = options.jobsRequest ?? false;

    if (!existingConsent?.id) {
      if (!granted) {
        const unchangedConsent: ConsentResponse = {
          id: existingConsent?.id ?? null,
          consentType,
          granted: false,
          version: existingConsent?.version ?? options.version ?? '1.0',
        };

        return of(unchangedConsent).pipe(
          tap((updatedConsent) => this.updateUserConsentsInStorage(updatedConsent))
        );
      }

      const request: CreateConsentRequest = {
        consentType,
        granted,
        language: options.language ?? 'EN',
        version: options.version ?? '1.0',
        purposeDescription: options.purposeDescription,
      };

      return this.createConsent(request, jobsRequest).pipe(
        tap((updatedConsent) => this.updateUserConsentsInStorage(updatedConsent))
      );
    }

    const request: UpdateConsentRequest = {
      granted,
      withdrawalReason: granted ? undefined : options.withdrawalReason ?? 'User preference change',
    };

    return this.updateConsent(existingConsent.id, request, jobsRequest).pipe(
      tap((updatedConsent) => this.updateUserConsentsInStorage(updatedConsent))
    );
  }

  /**
   * Get user from localStorage
   * @returns User object from localStorage or null
   */
  private getUserFromStorage(): StoredUser | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const userStr = localStorage.getItem('user');
    if (!userStr) {
      return null;
    }

    try {
      return JSON.parse(userStr) as StoredUser;
    } catch {
      return null;
    }
  }

  /**
   * Update user's consents in localStorage
   * @param updatedConsent The updated consent object
   */
  updateUserConsentsInStorage(updatedConsent: ConsentResponse): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const userStr = localStorage.getItem('user');
    if (!userStr) {
      return;
    }

    try {
      const user = JSON.parse(userStr);

      // Initialize consents array if it doesn't exist
      if (!user.consents) {
        user.consents = [];
      }

      // Find and update the consent in the array
      const consentIndex = user.consents.findIndex((c: UserConsent) => c.consentType === updatedConsent.consentType);

      const isGranted = updatedConsent.granted === true && !updatedConsent.withdrawnAt;

      if (consentIndex !== -1) {
        if (!isGranted) {
          // Remove withdrawn consent from array (user no longer has this consent)
          user.consents.splice(consentIndex, 1);
        } else {
          // Update existing consent with granted status
          user.consents[consentIndex] = {
            consentType: updatedConsent.consentType,
            grantedAt: updatedConsent.grantedAt,
          };
        }
      } else if (isGranted) {
        // Add new consent only if it's granted (not withdrawn)
        user.consents.push({
          consentType: updatedConsent.consentType,
          grantedAt: updatedConsent.grantedAt,
        });
      }

      // Also update the legacy boolean fields for backward compatibility
      switch (updatedConsent.consentType) {
        case ConsentType.AI_MATCHING:
          user.aiMatching = isGranted;
          user.openMatching = isGranted; // Some parts of the app use openMatching
          break;
        case ConsentType.AI_PARSING:
          user.aiParsing = isGranted;
          user.aiParsingConsent = isGranted;
          break;
        case ConsentType.MARKETING:
          user.marketing = isGranted;
          break;
        case ConsentType.PRIVACY_POLICY:
          user.privacyPolicy = isGranted;
          break;
      }

      // Save updated user back to localStorage
      localStorage.setItem('user', JSON.stringify(user));

      // Emit consent change event so components can react
      this.consentChangedSubject.next(updatedConsent.consentType);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Check if user has a specific consent
   * @param consentType The consent type to check
   * @returns true if user has granted the consent, false otherwise
   */
  hasConsent(consentType: ConsentType): boolean {
    const user = this.getUserFromStorage();
    if (!user) {
      return false;
    }
    // Check consents array
    if (user.consents && Array.isArray(user.consents)) {
      const consent = user.consents.find(c => c.consentType === consentType);
      if (consent && consent.grantedAt) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if user has AI_MATCHING consent
   * @returns true if user has granted AI_MATCHING consent
   */
  hasAiMatchingConsent(): boolean {
    return this.hasConsent(ConsentType.AI_MATCHING);
  }

  /**
   * Check if user has MARKETING consent
   * @returns true if user has granted MARKETING consent
   */
  hasMarketingConsent(): boolean {
    return this.hasConsent(ConsentType.MARKETING);
  }

  /**
   * Check if user has PRIVACY_POLICY consent
   * @returns true if user has granted PRIVACY_POLICY consent
   */
  hasPrivacyPolicyConsent(): boolean {
    return this.hasConsent(ConsentType.PRIVACY_POLICY);
  }
}
